import { Video } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const FONT_BODY = { fontFamily: "'Crimson Text', Georgia, serif" };
const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const AddEventDialog = ({ open, onOpenChange, form, setForm, onSubmit, submitting }) => {
  const isBibleStudy = form.title.toLowerCase().includes('bible study');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="add-event-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl text-blue-800">Add New Event</DialogTitle>
          <DialogDescription style={FONT_BODY}>Fill in the event details below.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label style={FONT_BODY} className="text-blue-800 font-semibold">Event Title *</Label>
            <Input
              data-testid="event-title-input"
              value={form.title}
              onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="e.g. Sunday Service, Bible Study"
              className="mt-1 border-2 border-blue-200"
              style={FONT_BODY}
            />
            {isBibleStudy && (
              <p className="text-blue-600 text-sm mt-1" style={FONT_BODY}>
                <Video className="h-3.5 w-3.5 inline mr-1" />
                Zoom link will be automatically included
              </p>
            )}
          </div>

          <div>
            <Label style={FONT_BODY} className="text-blue-800 font-semibold">Time *</Label>
            <Input
              data-testid="event-time-input"
              value={form.time}
              onChange={(e) => setForm(f => ({ ...f, time: e.target.value }))}
              placeholder="e.g. 11:00 AM"
              className="mt-1 border-2 border-blue-200"
              style={FONT_BODY}
            />
          </div>

          <div>
            <Label style={FONT_BODY} className="text-blue-800 font-semibold">Description</Label>
            <Input
              data-testid="event-description-input"
              value={form.description}
              onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Optional description"
              className="mt-1 border-2 border-blue-200"
              style={FONT_BODY}
            />
          </div>

          <div className="flex items-center gap-3">
            <Checkbox
              id="recurring"
              data-testid="event-recurring-checkbox"
              checked={form.recurring}
              onCheckedChange={(checked) => setForm(f => ({
                ...f, recurring: checked, recurrence_type: '', day_of_week: '', nth_week: '', date: '',
              }))}
            />
            <Label htmlFor="recurring" style={FONT_BODY} className="text-blue-800 font-semibold cursor-pointer">
              This is a recurring event
            </Label>
          </div>

          {form.recurring ? (
            <RecurringFields form={form} setForm={setForm} />
          ) : (
            <div>
              <Label style={FONT_BODY} className="text-blue-800 font-semibold">Date *</Label>
              <Input
                data-testid="event-date-input"
                type="date"
                value={form.date}
                onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))}
                className="mt-1 border-2 border-blue-200"
                style={FONT_BODY}
              />
            </div>
          )}

          {!isBibleStudy && (
            <div>
              <Label style={FONT_BODY} className="text-blue-800 font-semibold">Zoom Link (optional)</Label>
              <Input
                data-testid="event-zoom-input"
                value={form.zoom_link}
                onChange={(e) => setForm(f => ({ ...f, zoom_link: e.target.value }))}
                placeholder="https://zoom.us/..."
                className="mt-1 border-2 border-blue-200"
                style={FONT_BODY}
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            data-testid="event-submit-btn"
            onClick={onSubmit}
            disabled={submitting}
            className="bg-yellow-500 hover:bg-yellow-600 text-blue-900 font-bold px-6"
            style={FONT_BODY}
          >
            {submitting ? 'Adding...' : 'Add Event'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RecurringFields = ({ form, setForm }) => (
  <>
    <div>
      <Label style={FONT_BODY} className="text-blue-800 font-semibold">Recurrence Type *</Label>
      <Select value={form.recurrence_type} onValueChange={(val) => setForm(f => ({ ...f, recurrence_type: val }))}>
        <SelectTrigger data-testid="recurrence-type-select" className="mt-1 border-2 border-blue-200" style={FONT_BODY}>
          <SelectValue placeholder="Select recurrence" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="weekly" style={FONT_BODY}>Every week</SelectItem>
          <SelectItem value="monthly_nth" style={FONT_BODY}>Specific week of month</SelectItem>
        </SelectContent>
      </Select>
    </div>

    <div>
      <Label style={FONT_BODY} className="text-blue-800 font-semibold">Day of Week *</Label>
      <Select value={form.day_of_week} onValueChange={(val) => setForm(f => ({ ...f, day_of_week: val }))}>
        <SelectTrigger data-testid="day-of-week-select" className="mt-1 border-2 border-blue-200" style={FONT_BODY}>
          <SelectValue placeholder="Select day" />
        </SelectTrigger>
        <SelectContent>
          {DAYS_OF_WEEK.map((day, i) => (
            <SelectItem key={day} value={String(i)} style={FONT_BODY}>{day}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    {form.recurrence_type === 'monthly_nth' && (
      <div>
        <Label style={FONT_BODY} className="text-blue-800 font-semibold">Which week? *</Label>
        <Select value={form.nth_week} onValueChange={(val) => setForm(f => ({ ...f, nth_week: val }))}>
          <SelectTrigger data-testid="nth-week-select" className="mt-1 border-2 border-blue-200" style={FONT_BODY}>
            <SelectValue placeholder="Select week" />
          </SelectTrigger>
          <SelectContent>
            {['1st', '2nd', '3rd', '4th', '5th'].map((label, i) => (
              <SelectItem key={label} value={String(i + 1)} style={FONT_BODY}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    )}
  </>
);
